/**
 * THIS IS A SINGLETON.
 * It is to be instantiated only once.
 * Please use state/toast instead.
 */
import * as React from 'react';
import { createPortal } from 'react-dom';
import { Transition } from '@headlessui/react';
import { MessageDescriptor } from '@lingui/core';
import Icon from 'components/Icon';
import { Link } from 'react-router';
import toastNotification, { removeToast } from 'state/toast';
import { useLingui } from '@lingui/react';
import { getToastRenderTarget } from 'utils/ui/toastPortal';

export type TToastType = 'success' | 'link' | 'info' | 'warn' | 'error';

type TToastTypeStyles = {
  [key in TToastType]: {
    icon: string | string[];
    borderColor: string;
    bgColor: string;
    textColor: string;
  };
};

const toastStyle: TToastTypeStyles = {
  success: {
    icon: 'ex3d-icon--success bg-green-700 text-green-700',
    borderColor: 'var(--ex3d-dealer-primary-light-700)',
    bgColor: 'var(--ex3d-dealer-primary-light-100)',
    textColor: 'var(--ex3d-dealer-primary-light-700)',
  },
  link: {
    icon: 'ex3d-icon--external-link bg-teal text-gray-400',
    borderColor: 'var(--ex3d-dealer-primary)',
    bgColor: 'var(--ex3d-dealer-white)',
    textColor: 'var(--ex3d-dealer-primary)',
  },
  info: {
    icon: 'ex3d-icon--info bg-blue-700 text-blue-700',
    borderColor: 'var(--ex3d-dealer-blue-700)',
    bgColor: 'var(--ex3d-dealer-blue-100)',
    textColor: 'var(--ex3d-dealer-blue-700)',
  },
  warn: {
    icon: 'ex3d-icon--warning bg-orange-800 text-orange-800',
    borderColor: 'var(--ex3d-dealer-orange-800)',
    bgColor: 'var(--ex3d-dealer-orange-100)',
    textColor: 'var(--ex3d-dealer-orange-800)',
  },
  error: {
    icon: 'ex3d-icon--error bg-red-700 text-red-700',
    borderColor: 'var(--ex3d-dealer-red-700)',
    bgColor: 'var(--ex3d-dealer-red-100)',
    textColor: 'var(--ex3d-dealer-red-700)',
  },
};
export interface IToastProps {
  type: TToastType;
  title: string | MessageDescriptor;
  notificationId: string;
  message?: string | MessageDescriptor;
  linkText?: string;
  to?: string;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => boolean | undefined;
  newTab?: boolean;
  timeout?: number;
  onToggle?: (show: boolean) => void;
}

const ANIMATION_DURATION = 300;

export default function Toast() {
  const i18n = useLingui();
  const translatedMessage = (message: string | MessageDescriptor): string =>
    typeof message === 'string' ? message : i18n._(message);
  const state = toastNotification.useState();
  const [removingToasts, setRemovingToasts] = React.useState<Set<string>>(new Set());
  const timersRef = React.useRef<Map<string, NodeJS.Timeout>>(new Map());

  /**
   * Handles toast removal with exit animation.
   *
   * Business Logic:
   * 1. Mark toast as "removing" to trigger exit animation (Headless UI Transition)
   * 2. Wait for animation to complete (ANIMATION_DURATION = 300ms)
   * 3. Remove toast from global state (removeToast) - triggers React re-render
   * 4. Clean up local "removing" state to prevent memory leaks
   * 5. Clean up timer reference to prevent memory leaks
   *
   * This ensures smooth UX - users see the toast slide out before it's removed from the DOM.
   */
  const removeToastWithAnimation = React.useCallback((notificationId: string) => {
    // Step 1: Mark toast as removing - this triggers the exit animation via Transition's show={!isRemoving}
    setRemovingToasts((prev) => new Set(prev).add(notificationId));

    // Step 2-5: Wait for animation, then clean up state
    setTimeout(() => {
      removeToast(notificationId); // Step 3: Remove from global toast stack
      setRemovingToasts((prev) => {
        // Step 4: Clean up local removing state
        const next = new Set(prev);
        next.delete(notificationId);
        return next;
      });
      timersRef.current.delete(notificationId); // Step 5: Clean up timer reference
    }, ANIMATION_DURATION);
  }, []);

  // Reset rendering flag when Toast unmounts (cleanup)
  React.useEffect(() => {
    const timers = timersRef.current;
    return () => {
      toastNotification.setValues({ isBeingRendered: false });
      // Clear all timers on unmount
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  /**
   * Auto-removal timeout logic for each toast.
   *
   * Business Logic:
   * - Each toast has a timeout property (default: 5000ms, set to 0 to disable auto-removal)
   * - When a new toast appears, schedule its auto-removal
   * - Use useRef Map to track timers per notificationId (prevents recreating timers on re-renders)
   * - Clean up timers for toasts that were manually closed or removed
   *
   * Why useRef Map?
   * - Prevents timer duplication: If the same toast ID already has a timer, don't create a new one
   * - Survives re-renders: Unlike useState, useRef doesn't cause re-renders when updated
   * - Enables cleanup: When a toast is removed, we can clear its specific timer
   */
  React.useEffect(() => {
    // For each toast in the stack, set up auto-removal timer if not already exists
    state.toasts.forEach((toast) => {
      // Skip if timer already exists for this toast (prevents duplicate timers)
      if (timersRef.current.has(toast.notificationId)) {
        return;
      }

      const timeout = toast.timeout ?? 5000; // Default to 5 seconds
      if (timeout !== 0) {
        // timeout=0 means "never auto-remove"
        const timer = setTimeout(() => {
          removeToastWithAnimation(toast.notificationId);
        }, timeout);
        timersRef.current.set(toast.notificationId, timer);
      }
    });

    // Cleanup: Remove timers for toasts that no longer exist in state
    // This handles cases where toasts are manually closed before their timeout expires
    const currentToastIds = new Set(state.toasts.map((t) => t.notificationId));
    timersRef.current.forEach((timer, id) => {
      if (!currentToastIds.has(id)) {
        clearTimeout(timer); // Cancel the timer
        timersRef.current.delete(id); // Remove from tracking Map
      }
    });
  }, [state.toasts, removeToastWithAnimation]);

  /**
   * Handles closing a toast when its link is clicked.
   *
   * Business Logic:
   * - If the toast has a link (toast.to), clicking it should close the toast
   * - Custom onClick handlers can prevent auto-close by returning false
   * - This allows toasts to have interactive links that navigate AND close the toast
   *
   * @param notificationId - ID of the toast to close
   * @param onClick - Optional custom click handler from toast props
   * @returns Event handler for the link click
   */
  const handleClose = React.useCallback(
    (notificationId: string, onClick?: (e?: React.MouseEvent<HTMLElement>) => boolean | undefined) => {
      return (event: React.MouseEvent<HTMLElement>) => {
        let response: boolean | undefined = true;
        if (onClick) {
          // Call custom handler if provided - it can prevent closing by returning false
          response = onClick(event);
        }

        // Only close the toast if custom handler didn't return false
        if (response !== false) {
          removeToastWithAnimation(notificationId);
        }
      };
    },
    [removeToastWithAnimation]
  );

  // Render toast in isolated shadow DOM portal to escape web component CSS containment
  // while preventing style conflicts with the host page
  const toastContent = (
    <>
      {/* Global notification live region, rendered in isolated shadow DOM */}
      <aside
        data-testid="toast-component"
        role="dialog"
        aria-live="assertive"
        className="pointer-events-none fixed inset-0 z-[101] flex items-end px-4 py-6 sm:items-start sm:p-6"
      >
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          {state.toasts.map((toast) => {
            const styles = toastStyle[toast.type ?? 'info'];
            const isRemoving = removingToasts.has(toast.notificationId);
            return (
              <Transition key={toast.notificationId} show={!isRemoving} appear>
                <div
                  part="toast"
                  className="pointer-events-auto w-full overflow-hidden border-2 shadow-lg ring-1 ring-black/5 transition-all duration-300 ease-in-out data-[closed]:translate-x-full data-[enter]:translate-x-0 data-[closed]:opacity-0 data-[enter]:opacity-100 sm:data-[closed]:translate-y-0"
                  style={{
                    borderColor: `var(--ex3d-toast-border-color, ${styles.borderColor})`,
                    backgroundColor: `var(--ex3d-toast-bg-color, ${styles.bgColor})`,
                    borderRadius: 'var(--ex3d-toast-border-radius, 0.5rem)',
                    fontFamily: 'var(--ex3d-toast-font-family, inherit)',
                    maxWidth: 'var(--ex3d-toast-max-width, 28rem)',
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-start">
                      <div className="shrink-0">
                        <Icon part="toast-icon" aria-hidden="true" className={`${styles.icon} size-6`} />
                      </div>
                      <div className="ml-3 w-0 flex-1 pt-0.5">
                        <p
                          part="toast-title"
                          className="text-sm font-bold"
                          style={{ color: `var(--ex3d-toast-text-color, ${styles.textColor})` }}
                        >
                          {translatedMessage(toast.title)}
                        </p>
                        {toast.message && (
                          <p part="toast-message" className="mt-1 text-sm text-gray-900">
                            {translatedMessage(toast.message)}
                          </p>
                        )}
                        {toast.to && (
                          <div className="mt-3 flex space-x-7">
                            <Link
                              part="toast-link"
                              to={toast.to ?? ''}
                              onClick={handleClose(toast.notificationId, toast.onClick)}
                              target={toast.newTab ? '_blank' : undefined}
                              type="button"
                              className="rounded-md text-sm font-bold text-blue-700 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                            >
                              {toast.linkText}
                            </Link>
                          </div>
                        )}
                      </div>
                      <div className="ml-4 flex shrink-0">
                        <button
                          part="toast-close"
                          type="button"
                          data-testid="toast-close-button"
                          onClick={() => removeToastWithAnimation(toast.notificationId)}
                          className="inline-flex rounded-md hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:ring-offset-2"
                          style={{
                            color: 'var(--ex3d-toast-close-icon-color, var(--ex3d-dealer-gray-900))',
                          }}
                        >
                          <span className="sr-only">Close</span>
                          <Icon aria-hidden="true" className="ex3d-icon--close size-5 bg-teal" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Transition>
            );
          })}
        </div>
      </aside>
    </>
  );

  return createPortal(toastContent, getToastRenderTarget());
}
